import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';

const AdminLogin = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError(t('admin.login.error'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('Login successful for:', user.email);
      console.log('Environment admin email:', process.env.REACT_APP_ADMIN_EMAIL);
      
      // Check if user is admin (you can add custom claims or check specific admin emails)
      // Temporarily allow all authenticated users for testing
      console.log('Admin access granted for:', user.email);
      onLoginSuccess(user);
      
      // Uncomment below for production with proper admin checks
      /*
      if (user.email === process.env.REACT_APP_ADMIN_EMAIL || 
          user.email === 'admin@centron.com' ||
          user.email.endsWith('@centronaerospace.com') ||
          user.email.endsWith('@centron.com')) {
        console.log('Admin access granted for:', user.email);
        onLoginSuccess(user);
      } else {
        console.log('Access denied for:', user.email);
        await auth.signOut();
        setError(t('admin.login.access.denied'));
      }
      */
    } catch (error) {
      console.error('Login error:', error);
      switch (error.code) {
        case 'auth/user-not-found':
          setError(t('admin.login.error'));
          break;
        case 'auth/wrong-password':
          setError(t('admin.login.error'));
          break;
        case 'auth/invalid-email':
          setError(t('admin.login.error'));
          break;
        case 'auth/too-many-requests':
          setError(t('admin.login.error'));
          break;
        default:
          setError(t('admin.login.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8"
      >
        <div>
          <div className="mx-auto h-12 w-12 bg-accent-yellow rounded-full flex items-center justify-center">
            <svg className="h-8 w-8 text-dark-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-dark-grey">
            {t('admin.login.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('admin.login.subtitle')}
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-accent-yellow focus:border-accent-yellow focus:z-10 sm:text-sm"
                placeholder={t('admin.login.email')}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-accent-yellow focus:border-accent-yellow focus:z-10 sm:text-sm"
                placeholder={t('admin.login.password')}
              />
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm"
            >
              {error}
            </motion.div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-dark-grey bg-accent-yellow hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-yellow disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-dark-grey mr-2"></div>
                  {t('admin.login.loading')}
                </div>
              ) : (
                t('admin.login.submit')
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              ⚠️ This area is restricted to authorized personnel only.
            </p>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminLogin; 